/**
 * Translation dictionary.
 *
 * Single source of truth for every user-visible string we want to
 * localize. Mirrors the lemon_agent shape (`"area.key": "..."`) so we
 * can lift / share strings between the two consoles when it makes
 * sense.
 *
 * Conventions:
 *
 * - Keys are dotted: `area.subarea.label`. Keep the namespace stable
 *   even if the copy churns — components shouldn't need to know about
 *   re-wordings.
 * - English is the source. Adding a new key requires both `EN` and
 *   `ZH` entries (TS will yell at you if `Dict` doesn't match).
 * - `{name}` style placeholders. Single-pass replacement, no plurals
 *   yet (we'll add ICU when the first plural lands).
 * - Untranslated keys fall back to the English copy and emit a
 *   one-line `console.warn` in dev so we notice the gap.
 *
 * Scope today: just the chrome (sidebar, header) + the four new
 * Phase 1 pages (`/channels`, `/mcp`, `/teams`, `/skills`). Existing
 * pages (chat, agents, knowledge, llm, settings, evaluations) keep
 * their hard-coded English for now and will be retrofitted as we
 * iterate. That's intentional: shipping i18n on every existing page
 * doubles the diff and risks regressions in pages that already work.
 */

export type Lang = "en" | "zh";

export type Dict = Record<TranslationKey, string>;

// Listing keys here gives us compile-time exhaustiveness on the two
// dictionaries below. Add a new key once, both dicts must implement it.
export type TranslationKey =
  // chrome
  | "brand.name"
  | "brand.beta"
  | "header.signOut"
  | "toggle.lang"
  | "toggle.theme.light"
  | "toggle.theme.dark"
  | "toggle.theme.system"
  // sidebar nav
  | "nav.chat"
  | "nav.agents"
  | "nav.knowledge"
  | "nav.channels"
  | "nav.activity"
  | "nav.mcp"
  | "nav.skills"
  | "nav.llm"
  | "nav.teams"
  | "nav.workshop"
  | "nav.marketplace"
  | "nav.settings"
  | "nav.collapse"
  | "nav.expand"
  // workshop
  | "workshop.title"
  | "workshop.subtitle"
  // common
  | "common.eyebrow.workspace"
  | "common.loading"
  | "common.empty"
  | "common.comingSoon"
  | "common.copy"
  | "common.copied"
  | "common.refresh"
  | "common.cancel"
  | "common.save"
  // channels
  | "channels.title"
  | "channels.subtitle"
  | "channels.section.adapters"
  | "channels.section.adapters.desc"
  | "channels.section.bindings"
  | "channels.section.bindings.desc"
  | "channels.empty.bindings"
  | "channels.adapter.feishu"
  | "channels.adapter.feishu.desc"
  | "channels.adapter.web"
  | "channels.adapter.web.desc"
  | "channels.adapter.api"
  | "channels.adapter.api.desc"
  | "channels.status.available"
  | "channels.status.experimental"
  | "channels.status.planned"
  | "channels.action.configure"
  | "channels.action.create"
  | "channels.action.edit"
  | "channels.action.delete"
  | "channels.action.disable"
  | "channels.action.enable"
  | "channels.col.slug"
  | "channels.col.kind"
  | "channels.col.agent"
  | "channels.col.status"
  | "channels.col.credentials"
  | "channels.dialog.create"
  | "channels.dialog.edit"
  | "channels.field.slug"
  | "channels.field.slug.hint"
  | "channels.field.displayName"
  | "channels.field.agent"
  | "channels.field.config"
  | "channels.field.config.hint"
  | "channels.field.credentials"
  | "channels.field.credentials.hint"
  | "channels.field.credentials.kept"
  | "channels.field.credentials.drop"
  | "channels.creds.in"
  | "channels.creds.none"
  | "channels.binding.deleteConfirm"
  | "channels.toast.created"
  | "channels.toast.updated"
  | "channels.toast.deleted"
  | "channels.error.adminOnly"
  // mcp
  | "mcp.title"
  | "mcp.subtitle"
  | "mcp.section.exposed"
  | "mcp.section.exposed.desc"
  | "mcp.section.external"
  | "mcp.section.external.desc"
  | "mcp.empty.exposed"
  | "mcp.empty.exposed.cta"
  | "mcp.scope.user"
  | "mcp.scope.team"
  | "mcp.url.label"
  | "mcp.tools.count"
  | "mcp.external.cta"
  | "mcp.external.empty"
  | "mcp.external.col.name"
  | "mcp.external.col.transport"
  | "mcp.external.col.url"
  | "mcp.external.col.credentials"
  | "mcp.external.col.status"
  | "mcp.external.dialog.create"
  | "mcp.external.dialog.edit"
  | "mcp.external.field.slug"
  | "mcp.external.field.slug.hint"
  | "mcp.external.field.displayName"
  | "mcp.external.field.description"
  | "mcp.external.field.transport"
  | "mcp.external.field.url"
  | "mcp.external.field.url.hint"
  | "mcp.external.field.headers"
  | "mcp.external.field.headers.hint"
  | "mcp.external.field.headers.kept"
  | "mcp.external.field.headers.drop"
  | "mcp.external.deleteConfirm"
  | "mcp.external.toast.created"
  | "mcp.external.toast.updated"
  | "mcp.external.toast.deleted"
  | "mcp.external.error.adminOnly"
  // teams
  | "teams.title"
  | "teams.subtitle"
  | "teams.section.mine"
  | "teams.section.mine.desc"
  | "teams.section.members"
  | "teams.section.members.desc"
  | "teams.active"
  | "teams.switchTo"
  | "teams.create"
  | "teams.addMember"
  | "teams.col.email"
  | "teams.col.name"
  | "teams.col.role"
  | "teams.col.joined"
  | "teams.role.admin"
  | "teams.role.editor"
  | "teams.role.viewer"
  // skills
  | "skills.title"
  | "skills.subtitle"
  | "skills.tab.catalog"
  | "skills.tab.installed"
  | "skills.section.catalog.desc"
  | "skills.section.installed.empty"
  | "skills.search"
  | "skills.install"
  | "skills.installing"
  | "skills.installed"
  | "skills.uninstall"
  | "skills.uninstalling"
  | "skills.uninstall.confirm"
  | "skills.toast.installed"
  | "skills.toast.uninstalled"
  | "skills.toast.installError"
  | "skills.toast.uninstallError"
  // marketplace
  | "marketplace.title"
  | "marketplace.subtitle"
  | "marketplace.tab.datapack"
  | "marketplace.tab.agents"
  | "marketplace.search"
  | "marketplace.section.datapack.desc"
  | "marketplace.section.agents.desc"
  | "marketplace.section.empty"
  | "marketplace.section.installed"
  | "marketplace.install"
  | "marketplace.installed"
  | "marketplace.installing"
  | "marketplace.uninstall"
  | "marketplace.uninstalling"
  | "marketplace.uninstall.confirm"
  | "marketplace.toast.installed"
  | "marketplace.toast.uninstalled"
  | "marketplace.kb.note"
  | "marketplace.bundles"
  | "marketplace.vendor"
  | "marketplace.firstParty"
  | "marketplace.backend.notice"
  // approvals
  | "nav.approvals"
  | "approvals.title"
  | "approvals.subtitle"
  | "approvals.empty"
  | "approvals.empty.cta"
  | "approvals.col.tool"
  | "approvals.col.agent"
  | "approvals.col.requested"
  | "approvals.col.expires"
  | "approvals.col.risk"
  | "approvals.col.status"
  | "approvals.col.actions"
  | "approvals.filter.status"
  | "approvals.filter.status.all"
  | "approvals.filter.agent"
  | "approvals.filter.agent.all"
  | "approvals.status.pending"
  | "approvals.status.approved"
  | "approvals.status.denied"
  | "approvals.status.timeout"
  | "approvals.status.cancelled"
  | "approvals.risk.safe"
  | "approvals.risk.risky"
  | "approvals.risk.dangerous"
  | "approvals.action.approve"
  | "approvals.action.deny"
  | "approvals.action.viewRun"
  | "approvals.note.label"
  | "approvals.note.placeholder"
  | "approvals.drawer.tool"
  | "approvals.drawer.toolCallId"
  | "approvals.drawer.arguments"
  | "approvals.drawer.reason"
  | "approvals.drawer.required.role"
  | "approvals.drawer.requestedBy"
  | "approvals.drawer.decided"
  | "approvals.drawer.notice.cantResolve"
  | "approvals.toast.approved"
  | "approvals.toast.denied"
  | "approvals.toast.conflict"
  | "approvals.toast.forbidden"
  | "approvals.chat.banner.one"
  | "approvals.chat.banner.many"
  | "approvals.chat.banner.cta";

export const EN: Dict = {
  // chrome
  "brand.name": "knode-os",
  "brand.beta": "BETA",
  "header.signOut": "Sign out",
  "toggle.lang": "Toggle language",
  "toggle.theme.light": "Light",
  "toggle.theme.dark": "Dark",
  "toggle.theme.system": "System",
  // sidebar nav
  "nav.chat": "Chat",
  "nav.agents": "Agents",
  "nav.knowledge": "Knowledge",
  "nav.channels": "Channels",
  "nav.activity": "Activity",
  "nav.mcp": "MCP",
  "nav.skills": "Skills",
  "nav.llm": "LLM",
  "nav.teams": "Teams",
  "nav.workshop": "Workshop",
  "nav.marketplace": "Marketplace",
  "nav.settings": "Settings",
  "nav.collapse": "Collapse sidebar",
  "nav.expand": "Expand sidebar",
  // workshop
  "workshop.title": "Workshop",
  "workshop.subtitle":
    "Build interactive analysis pages on top of your knowledge bases — describe what you want to see and we generate a page with metric cards, charts, editable tables and lists. Pages persist across sessions.",
  // common
  "common.eyebrow.workspace": "workspace",
  "common.loading": "Loading…",
  "common.empty": "Nothing here yet.",
  "common.comingSoon": "Coming soon",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.refresh": "Refresh",
  "common.cancel": "Cancel",
  "common.save": "Save",
  // channels
  "channels.title": "Channels",
  "channels.subtitle":
    "Bind agents to inbound channels — chat apps, custom widgets, and the public API. Each channel adapter handles signature verification, message normalisation, and streaming output.",
  "channels.section.adapters": "Available adapters",
  "channels.section.adapters.desc":
    "Adapter packages that ship with knode-os. Marked “planned” adapters are scaffolded and will land in upcoming releases.",
  "channels.section.bindings": "Your bindings",
  "channels.section.bindings.desc":
    "Live channel ↔ agent bindings for this team. Create one to start receiving messages from a channel.",
  "channels.empty.bindings":
    "No channel bindings yet. Pick an adapter above to wire up your first channel.",
  "channels.adapter.feishu": "Feishu / Lark",
  "channels.adapter.feishu.desc":
    "Long-lived WebSocket inbound, streaming Lark cards outbound. Includes a QR-based device onboarding flow.",
  "channels.adapter.web": "Web widget",
  "channels.adapter.web.desc": "Embeddable chat widget powered by the same SSE protocol as /chat.",
  "channels.adapter.api": "Public API",
  "channels.adapter.api.desc":
    "Direct access via the OpenAI Responses-compatible endpoint. Already live — manage keys under Settings → API keys.",
  "channels.status.available": "Available",
  "channels.status.experimental": "Experimental",
  "channels.status.planned": "Planned",
  "channels.action.configure": "Configure",
  "channels.action.create": "Create binding",
  "channels.action.edit": "Edit",
  "channels.action.delete": "Delete",
  "channels.action.disable": "Disable",
  "channels.action.enable": "Enable",
  "channels.col.slug": "Slug",
  "channels.col.kind": "Adapter",
  "channels.col.agent": "Agent",
  "channels.col.status": "Status",
  "channels.col.credentials": "Credentials",
  "channels.dialog.create": "New channel binding",
  "channels.dialog.edit": "Edit channel binding",
  "channels.field.slug": "Slug",
  "channels.field.slug.hint": "A short identifier (e.g. feishu-prod). Unique within this team.",
  "channels.field.displayName": "Display name",
  "channels.field.agent": "Agent",
  "channels.field.config": "Adapter config (JSON)",
  "channels.field.config.hint":
    "Validated against the adapter's config schema. Leave as `{}` if the adapter doesn't take config.",
  "channels.field.credentials": "Credentials (JSON)",
  "channels.field.credentials.hint":
    "Encrypted at rest with versioned Fernet. Never returned over the API after save.",
  "channels.field.credentials.kept":
    "Stored credentials will be kept (leave blank). Provide a new JSON object to rotate.",
  "channels.field.credentials.drop": "Drop the stored credentials",
  "channels.creds.in": "in vault",
  "channels.creds.none": "none",
  "channels.binding.deleteConfirm":
    "Delete the “{slug}” binding? Inbound traffic to this channel will stop until you re-create it.",
  "channels.toast.created": "Created “{slug}”",
  "channels.toast.updated": "Updated “{slug}”",
  "channels.toast.deleted": "Deleted “{slug}”",
  "channels.error.adminOnly":
    "Only team admins can manage channel bindings — they carry secrets and route external traffic.",
  // mcp
  "mcp.title": "MCP",
  "mcp.subtitle":
    "Model Context Protocol surfaces. Expose your knowledge bases as MCP servers to external agents (Claude Desktop, Cursor, …) and register external MCP servers to enrich your own agents' tool palette.",
  "mcp.section.exposed": "MCP servers we expose",
  "mcp.section.exposed.desc":
    "Each MCP-enabled knowledge base gets a stable JSON-RPC endpoint. External agents authenticate with a kos_token (issue one under Settings → API keys).",
  "mcp.section.external": "External MCP servers",
  "mcp.section.external.desc":
    "Register external MCP servers to make their tools available to agents in this team.",
  "mcp.empty.exposed":
    "No MCP-exposed knowledge bases yet. Open the Knowledge page and toggle the “Expose as MCP” switch on a KB.",
  "mcp.empty.exposed.cta": "Manage knowledge bases",
  "mcp.scope.user": "personal",
  "mcp.scope.team": "team",
  "mcp.url.label": "MCP endpoint",
  "mcp.tools.count": "{n} tools",
  "mcp.external.cta": "Register external MCP",
  "mcp.external.empty":
    "No external MCP servers registered yet. Click “Register external MCP” to point your agents at a remote tool surface.",
  "mcp.external.col.name": "Name",
  "mcp.external.col.transport": "Transport",
  "mcp.external.col.url": "Endpoint",
  "mcp.external.col.credentials": "Credentials",
  "mcp.external.col.status": "Status",
  "mcp.external.dialog.create": "Register external MCP server",
  "mcp.external.dialog.edit": "Edit external MCP server",
  "mcp.external.field.slug": "Slug",
  "mcp.external.field.slug.hint":
    "A short identifier (e.g. filesystem-prod). Unique within this team.",
  "mcp.external.field.displayName": "Display name",
  "mcp.external.field.description": "Description",
  "mcp.external.field.transport": "Transport",
  "mcp.external.field.url": "JSON-RPC endpoint",
  "mcp.external.field.url.hint": "Must use http(s). The runtime will POST JSON-RPC to this URL.",
  "mcp.external.field.headers": "Auth headers (JSON)",
  "mcp.external.field.headers.hint":
    'Object of header → value. Common shape: `{"Authorization": "Bearer …"}`. Encrypted at rest, never returned over the API after save.',
  "mcp.external.field.headers.kept":
    "Stored auth headers will be kept (leave blank). Provide a new JSON object to rotate.",
  "mcp.external.field.headers.drop": "Drop the stored auth headers",
  "mcp.external.deleteConfirm":
    "Delete the “{slug}” MCP server? Agents will lose access to its tools immediately.",
  "mcp.external.toast.created": "Registered “{slug}”",
  "mcp.external.toast.updated": "Updated “{slug}”",
  "mcp.external.toast.deleted": "Deleted “{slug}”",
  "mcp.external.error.adminOnly":
    "Only team admins can manage external MCP servers — they carry secrets and define your agents' tool surface.",
  // teams
  "teams.title": "Teams",
  "teams.subtitle":
    "All teams you belong to, plus members of your active team. Switching teams here is the same as using the workspace switcher in the header.",
  "teams.section.mine": "My teams",
  "teams.section.mine.desc": "Every team that has granted you access. Click to switch into one.",
  "teams.section.members": "Members of {team}",
  "teams.section.members.desc":
    "People who can use this team's data and agents. Admins can change roles or remove members.",
  "teams.active": "Active",
  "teams.switchTo": "Switch",
  "teams.create": "New team",
  "teams.addMember": "Add member",
  "teams.col.email": "Email",
  "teams.col.name": "Name",
  "teams.col.role": "Role",
  "teams.col.joined": "Joined",
  "teams.role.admin": "Admin",
  "teams.role.editor": "Editor",
  "teams.role.viewer": "Viewer",
  // skills
  "skills.title": "Skills",
  "skills.subtitle":
    "Reusable capability packs your agents can pick up. A skill bundles a prompt fragment, sample tool calls, and an evaluation rubric — install one and any agent in this team can opt into it.",
  "skills.tab.catalog": "Catalog",
  "skills.tab.installed": "Installed",
  "skills.section.catalog.desc":
    "Skills shipped with knode-os plus community contributions you can install with one click.",
  "skills.section.installed.empty":
    "No skills installed yet. Browse the catalog and click Install on any skill to add it to your team.",
  "skills.search": "Search skills",
  "skills.install": "Install",
  "skills.installing": "Installing\u2026",
  "skills.installed": "Installed",
  "skills.uninstall": "Uninstall",
  "skills.uninstalling": "Uninstalling\u2026",
  "skills.uninstall.confirm":
    "Uninstall {name}? Agents that reference it will silently skip the skill until it's installed again.",
  "skills.toast.installed": "Installed {name}.",
  "skills.toast.uninstalled": "Uninstalled {name}.",
  "skills.toast.installError": "Failed to install {name}: {error}",
  "skills.toast.uninstallError": "Failed to uninstall {name}: {error}",
  // marketplace
  "marketplace.title": "Marketplace",
  "marketplace.subtitle":
    "Discover and install packaged capabilities sold by knode-os and partners — sector data packs, premium plugins, agent templates. Distinct from /skills, which manages the free system skills your team installs.",
  "marketplace.tab.datapack": "Data packs",
  "marketplace.tab.agents": "Agents",
  "marketplace.search": "Search the marketplace",
  "marketplace.section.datapack.desc":
    "Curated datasets bundled as a knowledge base. Install one and the bundled KB shows up under /knowledge — your agents can cite it through kb_search immediately.",
  "marketplace.section.agents.desc":
    "Ready-made agent templates with system prompt + tool wiring + recommended LLM config baked in. Installing one clones a draft under /agents you can edit.",
  "marketplace.section.empty": "Nothing matches your search yet.",
  "marketplace.section.installed": "Installed",
  "marketplace.install": "Install",
  "marketplace.installed": "Installed",
  "marketplace.installing": "Installing\u2026",
  "marketplace.uninstall": "Uninstall",
  "marketplace.uninstalling": "Uninstalling\u2026",
  "marketplace.uninstall.confirm":
    "Uninstall {name}? Agents that already cite it will lose access until you reinstall.",
  "marketplace.toast.installed": "Installed {name}.",
  "marketplace.toast.uninstalled": "Uninstalled {name}.",
  "marketplace.kb.note": "Mounts as KB `{slug}` on install.",
  "marketplace.bundles": "What's included",
  "marketplace.vendor": "by {vendor}",
  "marketplace.firstParty": "knode-os",
  "marketplace.backend.notice":
    "Catalog is a static preview today. Install state is browser-local until the marketplace backend lands.",
  // approvals
  "nav.approvals": "Approvals",
  "approvals.title": "Approvals",
  "approvals.subtitle":
    "Review tool calls your agents proposed but didn't run yet. Approving releases the run; denying records the decision and surfaces a guardrail-blocked outcome.",
  "approvals.empty":
    "Nothing waiting on you. Approvals show up here whenever an agent's spec marks a tool as `require_approval` and the model proposes calling it.",
  "approvals.empty.cta": "Browse agents",
  "approvals.col.tool": "Tool",
  "approvals.col.agent": "Agent",
  "approvals.col.requested": "Requested",
  "approvals.col.expires": "Expires",
  "approvals.col.risk": "Risk",
  "approvals.col.status": "Status",
  "approvals.col.actions": "Actions",
  "approvals.filter.status": "Status",
  "approvals.filter.status.all": "All",
  "approvals.filter.agent": "Agent",
  "approvals.filter.agent.all": "All agents",
  "approvals.status.pending": "Pending",
  "approvals.status.approved": "Approved",
  "approvals.status.denied": "Denied",
  "approvals.status.timeout": "Timed out",
  "approvals.status.cancelled": "Cancelled",
  "approvals.risk.safe": "Safe",
  "approvals.risk.risky": "Risky",
  "approvals.risk.dangerous": "Dangerous",
  "approvals.action.approve": "Approve",
  "approvals.action.deny": "Deny",
  "approvals.action.viewRun": "Open run",
  "approvals.note.label": "Note (optional)",
  "approvals.note.placeholder": "Why are you approving / denying? Stays on the row for audit.",
  "approvals.drawer.tool": "Tool",
  "approvals.drawer.toolCallId": "Call id",
  "approvals.drawer.arguments": "Proposed arguments",
  "approvals.drawer.reason": "Why the runtime asked",
  "approvals.drawer.required.role": "Required role",
  "approvals.drawer.requestedBy": "Requested by",
  "approvals.drawer.decided": "Decided",
  "approvals.drawer.notice.cantResolve":
    "You don't have permission to resolve this approval. Required role: {role}.",
  "approvals.toast.approved": "Approved.",
  "approvals.toast.denied": "Denied.",
  "approvals.toast.conflict": "Already resolved by a teammate. Refreshing.",
  "approvals.toast.forbidden": "You can't resolve this approval ({role} required).",
  "approvals.chat.banner.one": "1 tool call is waiting on a human approval.",
  "approvals.chat.banner.many": "{n} tool calls are waiting on a human approval.",
  "approvals.chat.banner.cta": "Open approvals",
};

export const ZH: Dict = {
  // chrome
  "brand.name": "knode-os",
  "brand.beta": "测试版",
  "header.signOut": "退出登录",
  "toggle.lang": "切换语言",
  "toggle.theme.light": "浅色",
  "toggle.theme.dark": "深色",
  "toggle.theme.system": "跟随系统",
  // sidebar nav
  "nav.chat": "对话",
  "nav.agents": "智能体",
  "nav.knowledge": "知识库",
  "nav.channels": "渠道",
  "nav.activity": "运行记录",
  "nav.mcp": "MCP",
  "nav.skills": "技能",
  "nav.llm": "模型",
  "nav.teams": "团队",
  "nav.workshop": "工作台",
  "nav.marketplace": "应用市场",
  "nav.settings": "设置",
  "nav.collapse": "收起侧边栏",
  "nav.expand": "展开侧边栏",
  // workshop
  "workshop.title": "工作台",
  "workshop.subtitle":
    "在你的知识库之上搭建交互式分析页面——描述你想看到的维度，我们会自动生成包含指标卡、图表、可编辑表格和列表的页面，并自动留存。",
  // common
  "common.eyebrow.workspace": "工作区",
  "common.loading": "加载中…",
  "common.empty": "暂无内容",
  "common.comingSoon": "即将上线",
  "common.copy": "复制",
  "common.copied": "已复制",
  "common.refresh": "刷新",
  "common.cancel": "取消",
  "common.save": "保存",
  // channels
  "channels.title": "渠道",
  "channels.subtitle":
    "把智能体接入对外渠道——IM、自定义嵌入组件、公开 API。每个渠道适配器负责签名校验、消息归一化和流式输出。",
  "channels.section.adapters": "可用适配器",
  "channels.section.adapters.desc":
    "knode-os 内置的适配器列表。「规划中」表示已搭好脚手架，会在后续版本里上线。",
  "channels.section.bindings": "本团队绑定",
  "channels.section.bindings.desc":
    "当前团队的「渠道 ↔ 智能体」绑定关系。创建一个绑定即可开始接收来自渠道的消息。",
  "channels.empty.bindings": "还没有渠道绑定。在上面挑一个适配器开始配置。",
  "channels.adapter.feishu": "飞书 / Lark",
  "channels.adapter.feishu.desc":
    "长链 WebSocket 接收消息，流式卡片回复。包含基于二维码的设备授权流程。",
  "channels.adapter.web": "Web 嵌入组件",
  "channels.adapter.web.desc": "可嵌入第三方页面的对话组件，使用与 /chat 相同的 SSE 协议。",
  "channels.adapter.api": "公开 API",
  "channels.adapter.api.desc":
    "直接调用兼容 OpenAI Responses 的接口。已上线——在「设置 → API 密钥」管理密钥。",
  "channels.status.available": "可用",
  "channels.status.experimental": "实验中",
  "channels.status.planned": "规划中",
  "channels.action.configure": "配置",
  "channels.action.create": "新建绑定",
  "channels.action.edit": "编辑",
  "channels.action.delete": "删除",
  "channels.action.disable": "停用",
  "channels.action.enable": "启用",
  "channels.col.slug": "标识",
  "channels.col.kind": "适配器",
  "channels.col.agent": "智能体",
  "channels.col.status": "状态",
  "channels.col.credentials": "凭据",
  "channels.dialog.create": "新建渠道绑定",
  "channels.dialog.edit": "编辑渠道绑定",
  "channels.field.slug": "标识",
  "channels.field.slug.hint": "简短标识（如 feishu-prod），在团队内唯一。",
  "channels.field.displayName": "显示名称",
  "channels.field.agent": "绑定智能体",
  "channels.field.config": "适配器配置（JSON）",
  "channels.field.config.hint": "会用适配器的 config schema 校验。无需配置时填 `{}`。",
  "channels.field.credentials": "凭据（JSON）",
  "channels.field.credentials.hint": "用版本化 Fernet 加密保存，保存后不会再通过 API 返回。",
  "channels.field.credentials.kept": "保留已存的凭据（留空即可）。填写新的 JSON 即可轮换。",
  "channels.field.credentials.drop": "删除已存的凭据",
  "channels.creds.in": "已加密保存",
  "channels.creds.none": "无",
  "channels.binding.deleteConfirm":
    "确定删除「{slug}」绑定？删除后该渠道将停止接收消息，直到你重新创建。",
  "channels.toast.created": "已创建「{slug}」",
  "channels.toast.updated": "已更新「{slug}」",
  "channels.toast.deleted": "已删除「{slug}」",
  "channels.error.adminOnly": "只有团队管理员可以管理渠道绑定（涉及密钥与对外流量）。",
  // mcp
  "mcp.title": "MCP",
  "mcp.subtitle":
    "Model Context Protocol 接入面。把知识库以 MCP 服务的形式开放给外部智能体（Claude Desktop、Cursor 等），同时也可以注册外部 MCP 服务来扩展自家智能体的工具集。",
  "mcp.section.exposed": "本团队对外暴露的 MCP",
  "mcp.section.exposed.desc":
    "每个开启了 MCP 的知识库都会获得一个稳定的 JSON-RPC 端点。外部客户端用 kos_token 鉴权（在「设置 → API 密钥」生成）。",
  "mcp.section.external": "已注册的外部 MCP",
  "mcp.section.external.desc": "注册外部 MCP 服务，让它们提供的工具能被本团队的智能体调用。",
  "mcp.empty.exposed":
    "还没有以 MCP 形式暴露的知识库。打开「知识库」页面，在某个 KB 上开启「以 MCP 暴露」开关。",
  "mcp.empty.exposed.cta": "去管理知识库",
  "mcp.scope.user": "个人",
  "mcp.scope.team": "团队",
  "mcp.url.label": "MCP 端点",
  "mcp.tools.count": "{n} 个工具",
  "mcp.external.cta": "注册外部 MCP",
  "mcp.external.empty":
    "尚未注册任何外部 MCP 服务。点击「注册外部 MCP」让你的 Agent 接入远端工具。",
  "mcp.external.col.name": "名称",
  "mcp.external.col.transport": "传输",
  "mcp.external.col.url": "端点",
  "mcp.external.col.credentials": "凭据",
  "mcp.external.col.status": "状态",
  "mcp.external.dialog.create": "注册外部 MCP 服务",
  "mcp.external.dialog.edit": "编辑外部 MCP 服务",
  "mcp.external.field.slug": "标识",
  "mcp.external.field.slug.hint": "团队内唯一的简短标识，例如 filesystem-prod。",
  "mcp.external.field.displayName": "显示名",
  "mcp.external.field.description": "描述",
  "mcp.external.field.transport": "传输",
  "mcp.external.field.url": "JSON-RPC 端点",
  "mcp.external.field.url.hint": "必须为 http(s)，运行时将向该地址 POST JSON-RPC 请求。",
  "mcp.external.field.headers": "鉴权请求头（JSON）",
  "mcp.external.field.headers.hint":
    'Header → 值的 JSON 对象。常见写法：`{"Authorization": "Bearer …"}`。落库时使用 Fernet 加密，保存后接口不会再返回。',
  "mcp.external.field.headers.kept": "保留已保存的鉴权请求头（留空即可），填入新对象可轮换。",
  "mcp.external.field.headers.drop": "移除已保存的鉴权请求头",
  "mcp.external.deleteConfirm": "删除「{slug}」？Agent 将立即失去对其工具的访问权。",
  "mcp.external.toast.created": "已注册「{slug}」",
  "mcp.external.toast.updated": "已更新「{slug}」",
  "mcp.external.toast.deleted": "已删除「{slug}」",
  "mcp.external.error.adminOnly":
    "只有团队管理员可以管理外部 MCP 服务——它们承载密钥并定义 Agent 的工具集。",
  // teams
  "teams.title": "团队",
  "teams.subtitle":
    "你所在的全部团队，以及当前团队的成员。这里切换团队的效果与顶栏的工作区切换器一致。",
  "teams.section.mine": "我的团队",
  "teams.section.mine.desc": "所有授予了你访问权限的团队，点击即可切换进去。",
  "teams.section.members": "{team} 的成员",
  "teams.section.members.desc": "当前团队中能访问数据和智能体的人。管理员可以调整角色或移除成员。",
  "teams.active": "当前",
  "teams.switchTo": "切换",
  "teams.create": "新建团队",
  "teams.addMember": "添加成员",
  "teams.col.email": "邮箱",
  "teams.col.name": "姓名",
  "teams.col.role": "角色",
  "teams.col.joined": "加入时间",
  "teams.role.admin": "管理员",
  "teams.role.editor": "成员",
  "teams.role.viewer": "只读",
  // skills
  "skills.title": "技能",
  "skills.subtitle":
    "可复用的能力包，智能体可以「拿起来用」。一个技能打包了提示片段、样例工具调用和评测标准——安装后，本团队的任何智能体都可以选择启用。",
  "skills.tab.catalog": "应用市场",
  "skills.tab.installed": "已安装",
  "skills.section.catalog.desc": "knode-os 内置技能加上社区贡献的技能，一键安装。",
  "skills.section.installed.empty": "还没有安装任何技能。去应用市场看看，点击「安装」即可。",
  "skills.search": "搜索技能",
  "skills.install": "安装",
  "skills.installing": "安装中\u2026",
  "skills.installed": "已安装",
  "skills.uninstall": "卸载",
  "skills.uninstalling": "卸载中\u2026",
  "skills.uninstall.confirm":
    "确定卸载「{name}」吗？引用了它的智能体会自动跳过该技能，直到重新安装。",
  "skills.toast.installed": "已安装「{name}」。",
  "skills.toast.uninstalled": "已卸载「{name}」。",
  "skills.toast.installError": "安装「{name}」失败：{error}",
  "skills.toast.uninstallError": "卸载「{name}」失败：{error}",
  // marketplace
  "marketplace.title": "应用市场",
  "marketplace.subtitle":
    "发现并一键安装由 knode-os 与合作方售卖的能力组合——板块数据包、商业插件、智能体模板。与「技能」页面分工：技能用来管理团队自己安装的免费系统技能。",
  "marketplace.tab.datapack": "数据包",
  "marketplace.tab.agents": "智能体",
  "marketplace.search": "搜索应用市场",
  "marketplace.section.datapack.desc":
    "经过整理的数据集，安装即在团队下挂载一个知识库；你的 Agent 可以立刻通过 kb_search 引用并标注来源。",
  "marketplace.section.agents.desc":
    "现成的智能体模板，预置 system prompt、工具组合、推荐 LLM 配置。安装会在「智能体」里克隆一份草稿，你可以接着改。",
  "marketplace.section.empty": "没有匹配的结果。",
  "marketplace.section.installed": "已安装",
  "marketplace.install": "安装",
  "marketplace.installed": "已安装",
  "marketplace.installing": "安装中…",
  "marketplace.uninstall": "卸载",
  "marketplace.uninstalling": "卸载中…",
  "marketplace.uninstall.confirm": "卸载「{name}」？引用过它的 Agent 将无法访问，直至重新安装。",
  "marketplace.toast.installed": "已安装「{name}」。",
  "marketplace.toast.uninstalled": "已卸载「{name}」。",
  "marketplace.kb.note": "安装后挂载为知识库 `{slug}`。",
  "marketplace.bundles": "包含内容",
  "marketplace.vendor": "提供方：{vendor}",
  "marketplace.firstParty": "knode-os 官方",
  "marketplace.backend.notice":
    "目录为静态预览版本，安装状态仅保存在当前浏览器，等后端就绪后会切到正式接口。",
  // approvals
  "nav.approvals": "审批",
  "approvals.title": "工具调用审批",
  "approvals.subtitle":
    "审查 Agent 想调用、但被人审策略拦下的工具。批准后这次运行继续；拒绝则把决定记录下来，并把这次运行标记为 guardrail_blocked。",
  "approvals.empty":
    "目前没有需要你处理的审批。当 Agent Spec 把某个工具标记为 require_approval、模型又提议调用它时，审批会出现在这里。",
  "approvals.empty.cta": "查看 Agent",
  "approvals.col.tool": "工具",
  "approvals.col.agent": "Agent",
  "approvals.col.requested": "发起时间",
  "approvals.col.expires": "过期时间",
  "approvals.col.risk": "风险",
  "approvals.col.status": "状态",
  "approvals.col.actions": "操作",
  "approvals.filter.status": "状态",
  "approvals.filter.status.all": "全部",
  "approvals.filter.agent": "Agent",
  "approvals.filter.agent.all": "全部 Agent",
  "approvals.status.pending": "待审批",
  "approvals.status.approved": "已批准",
  "approvals.status.denied": "已拒绝",
  "approvals.status.timeout": "已超时",
  "approvals.status.cancelled": "已取消",
  "approvals.risk.safe": "安全",
  "approvals.risk.risky": "需注意",
  "approvals.risk.dangerous": "危险",
  "approvals.action.approve": "批准",
  "approvals.action.deny": "拒绝",
  "approvals.action.viewRun": "查看运行",
  "approvals.note.label": "备注（可选）",
  "approvals.note.placeholder": "为什么批准 / 拒绝？保留在这条审批上做审计。",
  "approvals.drawer.tool": "工具",
  "approvals.drawer.toolCallId": "Tool call id",
  "approvals.drawer.arguments": "提议的参数",
  "approvals.drawer.reason": "运行时给的理由",
  "approvals.drawer.required.role": "需要角色",
  "approvals.drawer.requestedBy": "发起人",
  "approvals.drawer.decided": "决定",
  "approvals.drawer.notice.cantResolve": "你没有权限处理这条审批。需要角色：{role}。",
  "approvals.toast.approved": "已批准。",
  "approvals.toast.denied": "已拒绝。",
  "approvals.toast.conflict": "已被同事处理，正在刷新。",
  "approvals.toast.forbidden": "你不能处理这条审批（需要 {role}）。",
  "approvals.chat.banner.one": "有 1 个工具调用正在等待人审。",
  "approvals.chat.banner.many": "有 {n} 个工具调用正在等待人审。",
  "approvals.chat.banner.cta": "去审批",
};

const TABLES: Record<Lang, Dict> = { en: EN, zh: ZH };

let warnedKeys: Set<string> | null = null;

/**
 * Render `key` in `lang`, with `{name}` placeholders interpolated.
 *
 * Falls back to English if the key is missing in the requested
 * language; falls back to the key itself if it's missing in EN too,
 * which would only happen if a caller types a key that no dict
 * declares (TS catches this at compile time, but the runtime guard
 * keeps things from rendering as empty cells).
 */
export function translate(
  lang: Lang,
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const table = TABLES[lang] ?? EN;
  let template = table[key] ?? EN[key];
  if (template === undefined) {
    // ``import.meta.env.DEV`` is true in `vite dev` / `vite preview` and
    // false in `vite build` production bundles. Vite tree-shakes the
    // whole branch in prod, so the warning only fires during dev.
    if (import.meta.env.DEV) {
      warnedKeys ??= new Set();
      if (!warnedKeys.has(key)) {
        warnedKeys.add(key);
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing translation key: ${key}`);
      }
    }
    return key;
  }
  if (!vars) return template;
  for (const [name, value] of Object.entries(vars)) {
    template = template.replaceAll(`{${name}}`, String(value));
  }
  return template;
}
